import axios from "axios";
import React, { useCallback, useReducer } from "react";

import { IFetcherRenderProps, TFetchFunction } from "../types";
import { useCancelTokenSource } from "./../../useCancelTokenSource";
import { ActionTypes, IFetcherState, initialState, reducer } from "./reducer";

/**
 * Accept async function
 *
 * @template P Async function params type
 * @template R Async function result type
 * @param {TFetchFunction<P, R>} fetchFunction
 * @param {P} [params] Parameters used on function invoke
 * @param {any[]} [deps]  If present, effect will only activate if the values in the list change.
 * @returns {IFetcherState<R>}
 */
export const useLazyFetcher = <P, R>(
  fetchFunction: TFetchFunction<P, R>,
  params: P,
  deps: any[] = [],
): IFetcherRenderProps<R> => {
  const { refreshToken, source } = useCancelTokenSource();

  const [state, dispatch] = useReducer<React.Reducer<IFetcherState<R>, ActionTypes<R>>>(
    reducer,
    initialState as IFetcherState<R>,
  );

  const handlePayloadUpdate = useCallback((payload: R) => {
    dispatch({ type: "FETCH_SUCCESS", payload });
  }, []);

  const fetchData = useCallback(async () => {
    refreshToken();
    dispatch({ type: "FETCH_START" });

    try {
      const result = await fetchFunction(params, { cancelToken: source.current.token });

      dispatch({ type: "FETCH_SUCCESS", payload: result });
    } catch (error) {
      if (!(axios.isCancel(error) || error.canceled)) {
        dispatch({ type: "FETCH_ERROR", error });
      }
    }
  }, deps);

  return { ...state, updatePayload: handlePayloadUpdate, fetch: fetchData };
};
